import { computed, reactive, readonly } from 'vue'

import { ApiError, authApi, setCsrfToken } from './api'
import type { AuthState } from './types'

interface SessionModel {
  ready: boolean
  loading: boolean
  authenticated: boolean
  kind: 'user' | 'admin' | null
  user: AuthState['user']
  error: string
}

const state = reactive<SessionModel>({
  ready: false,
  loading: false,
  authenticated: false,
  kind: null,
  user: null,
  error: '',
})

let bootstrapPromise: Promise<void> | null = null

declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        initData?: string
        ready?: () => void
        expand?: () => void
        setHeaderColor?: (color: string) => void
        setBackgroundColor?: (color: string) => void
      }
    }
  }
}

function telegramInitData(): string {
  const injected = window.Telegram?.WebApp?.initData?.trim()
  if (injected) return injected

  const query = new URLSearchParams(window.location.search)
  const direct = query.get('tgWebAppData')
  if (direct) return direct

  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''))
  return hash.get('tgWebAppData') ?? ''
}

function applyAuth(auth: AuthState): void {
  state.authenticated = auth.authenticated
  state.kind = auth.kind ?? null
  state.user = auth.user ?? null
  setCsrfToken(auth.csrfToken)
}

async function performBootstrap(force: boolean): Promise<void> {
  state.loading = true
  state.error = ''
  try {
    let auth = await authApi.me()
    const initData = telegramInitData()
    if (!auth.authenticated && initData) {
      auth = await authApi.initData(initData)
      window.Telegram?.WebApp?.ready?.()
      window.Telegram?.WebApp?.expand?.()
    }
    applyAuth(auth)
  } catch (error) {
    if (force || !(error instanceof ApiError && error.status === 401)) {
      state.error = error instanceof Error ? error.message : 'Не удалось проверить вход.'
    }
    applyAuth({ authenticated: false })
  } finally {
    state.ready = true
    state.loading = false
  }
}

export function bootstrapSession(force = false): Promise<void> {
  if (!bootstrapPromise || force) {
    bootstrapPromise = performBootstrap(force).finally(() => {
      bootstrapPromise = null
    })
  }
  return bootstrapPromise
}

export function acceptAuth(auth: AuthState): void {
  applyAuth(auth)
  state.ready = true
  state.error = ''
}

export async function logout(): Promise<void> {
  try {
    await authApi.logout()
  } finally {
    applyAuth({ authenticated: false })
  }
}

export function useSession() {
  return {
    state: readonly(state),
    isUser: computed(() => state.authenticated && state.kind === 'user'),
    isAdmin: computed(() => state.authenticated && state.kind === 'admin'),
    bootstrap: bootstrapSession,
    accept: acceptAuth,
    logout,
  }
}
